// Per-paper PDF export — browser side of Unit 25.
//
// The Konva overlay (AnnotationOverlay) registers each page's Stage into the
// module-level registry below on mount. `exportPaper()` walks the registry
// in page order, captures each Stage as a PNG via `toDataURL()`, and POSTs
// the bundle to /api/papers/{id}/export — where PyMuPDF stamps them onto
// the original scan with the 80pt right-margin extension (server-side
// Unit 25).
//
// Why a module-level registry instead of refs threaded through props:
// AnnotationOverlay sits inside ScanCanvas which iterates pages; lifting
// refs back up through React (forwardRef on a list) costs more code than
// a tiny `Map<pageNumber, Stage>` and the registry naturally cleans up
// when overlays unmount.

import type Konva from "konva";

import { apiFetch, ApiError } from "../api/client";

interface RegistryEntry {
  paperId: number;
  pageNumber: number;
  stage: Konva.Stage;
  /** Page-region size in CSS px (the Stage may be wider/taller — see below). */
  pageWidthPx: number;
  pageHeightPx: number;
}

const _registry = new Map<string, RegistryEntry>();

function key(paperId: number, pageNumber: number): string {
  return `${paperId}:${pageNumber}`;
}

export function registerOverlayStage(
  paperId: number,
  pageNumber: number,
  stage: Konva.Stage,
  pageWidthPx: number,
  pageHeightPx: number,
): void {
  _registry.set(key(paperId, pageNumber), {
    paperId,
    pageNumber,
    stage,
    pageWidthPx,
    pageHeightPx,
  });
}

/** Per-page overlay geometry sent to the backend so it can size the extended
 *  mediabox exactly. All values in CSS px. `stage_*` is the full Konva Stage
 *  (page + right margin + footnote area); `page_*` is just the page region.
 *  The backend extends the mediabox by the (stage − page) ratio so the page
 *  region of the PNG lands 1:1 over the original scan. `null` marks a page that
 *  hadn't rendered (blank placeholder PNG) — the backend keeps it unchanged. */
type PageGeometry = { stage_w: number; stage_h: number; page_w: number; page_h: number } | null;

export function unregisterOverlayStage(paperId: number, pageNumber: number): void {
  _registry.delete(key(paperId, pageNumber));
}

export interface ExportPaperResult {
  ok: true;
  path: string;
}

export class PaperExportError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
  }
}

// Minimal 1×1 transparent PNG — used as a no-op overlay for pages that
// haven't scrolled into view yet. PyMuPDF scales it to fill the extended
// mediabox, leaving the scan untouched beneath it.
const BLANK_PNG_DATA_URL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==";

/** Capture every registered overlay for this paper, POST as multipart.
 *  Pages that have not yet rendered (not scrolled into view) receive a
 *  transparent placeholder so the export always succeeds. */
export async function exportPaperPdf(
  paperId: number,
  expectedPageCount: number | null,
): Promise<ExportPaperResult> {
  // Collect entries for this paper, in page order.
  const entryMap = new Map(
    [..._registry.values()]
      .filter((e) => e.paperId === paperId)
      .map((e) => [e.pageNumber, e]),
  );

  if (entryMap.size === 0) {
    throw new PaperExportError("PDF not yet rendered — wait a moment for the pages to load, then try again.");
  }

  // Determine the range of pages to send. Use expectedPageCount when known;
  // fall back to whatever pages are registered.
  const pageCount = expectedPageCount ?? Math.max(...entryMap.keys());
  const blankBlob = dataUrlToBlob(BLANK_PNG_DATA_URL);

  const fd = new FormData();
  const geometry: PageGeometry[] = [];
  for (let p = 1; p <= pageCount; p++) {
    const entry = entryMap.get(p);
    if (entry) {
      const dataUrl = entry.stage.toDataURL({ pixelRatio: 2, mimeType: "image/png" });
      fd.append("overlay_pages", dataUrlToBlob(dataUrl), `page-${p}.png`);
      geometry.push({
        stage_w: entry.stage.width(),
        stage_h: entry.stage.height(),
        page_w: entry.pageWidthPx,
        page_h: entry.pageHeightPx,
      });
    } else {
      fd.append("overlay_pages", blankBlob, `page-${p}.png`);
      geometry.push(null);
    }
  }
  fd.append("overlay_geometry", JSON.stringify(geometry));

  let res: Response;
  try {
    res = await apiFetch(`/api/papers/${paperId}/export`, {
      method: "POST",
      body: fd,
      // Let the browser set the multipart boundary itself.
      headers: {},
    });
  } catch (err) {
    throw new PaperExportError("Network error while exporting.", err);
  }
  if (!res.ok) {
    const body = await safeJson(res);
    const detail = (body as { detail?: string } | null)?.detail ?? `Server returned ${res.status}.`;
    throw new PaperExportError(detail);
  }
  return (await res.json()) as ExportPaperResult;
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, body] = dataUrl.split(",", 2);
  const mime = header.match(/data:(.*?);base64/)?.[1] ?? "image/png";
  const binary = atob(body);
  const buf = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) buf[i] = binary.charCodeAt(i);
  return new Blob([buf], { type: mime });
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

// Re-export the ApiError type so consumers can `instanceof`-check uniformly.
export { ApiError };
